/**
 * Landing page content — single source of truth for marketing copy.
 *
 * Every claim here must stay truthful to what the product actually does
 * (see ARCHITECTURE.md §3 safety properties and CLAUDE.md §9.3 on the
 * zero-code gap for bundled serverless runtimes — the FAQ reflects it
 * honestly rather than marketing it away).
 */

import type { LucideIcon } from 'lucide-react';
import {
  BarChart3,
  Bell,
  Boxes,
  EyeOff,
  Gauge,
  Lock,
  Plug,
  ShieldCheck,
  TrendingUp,
  Zap,
} from 'lucide-react';

export interface LandingItem {
  icon: LucideIcon;
  title: string;
  copy: string;
}

/** Why a team picks Aicostr over a proxy — shown above the capability grid. */
export const STANDOUT: LandingItem[] = [
  {
    icon: Plug,
    title: 'One line. Zero config.',
    copy: 'Add a single import at the top of your app. Every provider your code already calls is detected and tracked automatically — no per-call changes.',
  },
  {
    icon: Gauge,
    title: 'Never in your request path',
    copy: 'No proxy, no shared keys. Aicostr observes from the side, so it cannot slow or break your AI calls — observer overhead stays under 2 ms.',
  },
  {
    icon: EyeOff,
    title: 'Never sees keys, prompts, or responses',
    copy: 'Receipts carry only token counts and metadata — never your API keys, prompts, or model output. The guarantee is enforced in CI.',
  },
  {
    icon: Boxes,
    title: 'Any SDK, any framework',
    copy: 'HTTP-level interception catches native SDKs, the Vercel AI SDK, LangChain, custom wrappers, even embeddings — with no per-integration work.',
  },
];

export const FEATURES: LandingItem[] = [
  {
    icon: BarChart3,
    title: 'Multi-provider visibility',
    copy: 'One dashboard for OpenAI, Anthropic, Google, Cohere, Mistral, and self-hosted models. Group by team, project, feature, or model.',
  },
  {
    icon: Zap,
    title: 'Real-time tracking',
    copy: 'Live spend updates with sub-2 ms observer overhead — no proxy in your request path. Watch the bill move as your agents run.',
  },
  {
    icon: TrendingUp,
    title: 'Forecasts you can trust',
    copy: 'End-of-month projections with confidence intervals, so finance signs off without three Slack threads.',
  },
  {
    icon: Bell,
    title: 'Anomaly detection',
    copy: 'A 2× spike against your 7-day baseline pings Slack within minutes. No silent burns.',
  },
  {
    icon: ShieldCheck,
    title: 'Workspace-grade access control',
    copy: 'Server-derived identity, six-role RBAC, scoped API keys, and a full audit log of every sensitive action.',
  },
  {
    icon: Lock,
    title: 'Hash-only API keys',
    copy: 'Tokens are SHA-256 hashed at rest, shown once, and rotatable or revocable anytime. Your secrets stay yours.',
  },
];

export interface PricingTier {
  name: string;
  price: string;
  cadence: string;
  blurb: string;
  badge?: string;
  highlighted?: boolean;
  cta: { label: string; primary: boolean };
  /** Small print under the price, e.g. metered overage. */
  footnote?: string;
  /** Annual billing variant — shown when the pricing toggle is on Annual. */
  annual?: {
    /** Effective per-month price when billed annually, e.g. '$24'. */
    price: string;
    /** Subline, e.g. 'billed annually · $290/yr'. */
    note: string;
    /** Savings badge, e.g. 'Save 17%'. */
    savings: string;
  };
  features: string[];
}

/**
 * Pricing tiers shown on the marketing page.
 *
 * ⚠️ DISPLAY-ONLY (not wired to the backend yet). These prices, the metered
 * overage line, and the monthly/annual billing toggle are *advertised* here but
 * NOT enforced. Future work: Stripe monthly + annual price IDs, the Billing
 * page, and real quota metering / overage charging. Until that's done, editing
 * these numbers changes only what visitors read — not what anyone is charged.
 */
export const PRICING: PricingTier[] = [
  {
    name: 'Free',
    price: '$0',
    cadence: '/month',
    blurb: 'Perfect for prototyping a side project.',
    cta: { label: 'Get started', primary: false },
    features: [
      '50,000 events / month',
      'All providers',
      '7-day data retention',
      '2 team seats',
      'Email alerts',
    ],
  },
  {
    name: 'Pro',
    price: '$29',
    cadence: '/month',
    blurb: 'For teams shipping production AI features.',
    badge: 'Most popular',
    highlighted: true,
    cta: { label: 'Start free trial', primary: true },
    footnote: 'then $20 per additional 1M events',
    annual: {
      price: '$24',
      note: 'billed annually · $290/yr',
      savings: 'Save 17%',
    },
    features: [
      '1M events / month included',
      'All providers',
      '90-day data retention',
      '5 team seats',
      'Forecasts + optimization tips',
      'Slack, email, webhook routing',
      'Priority support',
    ],
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    cadence: '',
    blurb: 'For organisations that need SSO and a SLA.',
    cta: { label: 'Contact sales', primary: false },
    features: [
      'Everything in Pro',
      'Volume event pricing',
      'SAML SSO + SCIM',
      'Configurable retention',
      'Unlimited seats',
      'Dedicated support + SLA',
      'Audit-log export',
    ],
  },
];

export interface Step {
  num: string;
  title: string;
  copy: string;
  /** Which language-specific snippet to render under this step, if any. */
  snippetKey?: 'install' | 'import';
}

export const STEPS: Step[] = [
  {
    num: '01',
    title: 'Install',
    copy: 'Add the package to your app — Python or Node, your choice.',
    snippetKey: 'install',
  },
  {
    num: '02',
    title: 'Import one line',
    copy: 'Drop a single import at the top of your entry file. Every provider you already call is detected automatically.',
    snippetKey: 'import',
  },
  {
    num: '03',
    title: 'Watch spend live',
    copy: 'Open the dashboard. Spend, forecasts, and anomaly alerts populate in real time — with under 2 ms observer overhead.',
  },
];

export interface FaqItem {
  q: string;
  a: string;
}

export const FAQ_ITEMS: FaqItem[] = [
  {
    q: 'Will Aicostr slow down or break my AI calls?',
    a: 'No. Aicostr is not a proxy and never sits in your request path — it observes from the side. If Aicostr were ever down, your AI calls would be completely unaffected. Observer overhead stays under 2 ms.',
  },
  {
    q: 'Does it ever see my API keys, prompts, or responses?',
    a: 'Never. Receipts carry only token counts and metadata. Your keys, prompts, and model outputs never leave your process. The guarantee is enforced by automated tests in CI.',
  },
  {
    q: 'Is it really zero-code? What about my framework?',
    a: 'One import covers most Node and Python runtimes — native SDKs, the Vercel AI SDK, LangChain, and custom wrappers are detected via HTTP-level interception. Some bundled serverless stacks (for example Next.js on Vercel with Sentry instrumentation in the mix) wrap the runtime in ways that defeat automatic patching; there we provide an explicit one-line forward to our /track endpoint as the supported fallback. Either way, it is a single integration point.',
  },
  {
    q: 'Which providers are supported?',
    a: 'OpenAI, Anthropic, Google Gemini, Cohere, and Mistral natively, plus OpenAI-compatible endpoints (xAI, Groq, Together, and more). Self-hosted models are tracked with custom pricing.',
  },
  {
    q: 'How does anomaly detection work?',
    a: 'Aicostr compares live spend to your rolling 7-day baseline. When usage crosses 2× expected, it routes an alert to Slack, email, or a webhook within minutes — so a runaway loop or a bad deploy never surfaces as a surprise invoice.',
  },
  {
    q: 'What does the free tier include?',
    a: 'Up to 10,000 events per month, 3 providers, 7-day retention, and email alerts — no credit card required. Upgrade to Pro for unlimited events, all providers, 90-day retention, and forecasting.',
  },
];
